// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
import { analyzePerEngineContext } from "./utils.js";
import {
  inferEngineControlTypeFromContext,
  inferEngineCoolingTypeFromContext,
  inferEngineFamilyFromContext,
  isRotaxEngineContext,
} from "../core/engine-family.js";
import {
  sensitivityModes,
} from "../core/config.js";
import {
  engineDescriptorsForData,
  engineScopedChannelLabel,
  engineScopedProbeLabel,
} from "../core/engines.js";
import {
  rawMetricDescriptor,
} from "../core/raw-metadata.js";
import {
  scoreClass,
  scoreLabel,
} from "../core/score.js";
import {
  normalizeSensitivity,
} from "../core/preferences.js";
import {
  healthMetricScopeForMetric,
  metricParticipatesInScopedScore,
  normalizeHealthMetricScope,
} from "../engine/review-model.js";
import {
  analyzeCarbIce,
} from "./carb-ice.js";
import {
  analyzeChtHealth,
} from "./cht.js";
import {
  analyzeChtRhythm,
} from "./cht-rhythm.js";
import {
  analyzeCombustionSignatures,
} from "./combustion.js";
import {
  analyzeAbnormalCombustion,
} from "./abnormal-combustion.js";
import {
  analyzeCrossEngineComparison,
  cgrTwinTimingProvenance,
} from "./cross-engine.js";
import {
  analyzeCylinderHarmony,
} from "./cylinder-harmony.js";
import {
  analyzeCylinderStartupSymmetry,
} from "./cylinder-startup-symmetry.js";
import {
  analyzeCylinderPersonality,
} from "./cylinder-personality.js";
import {
  analyzeChtCoolingRate,
  analyzeCoolingEfficiency,
  compactCoolingRateSummaryEvents,
} from "./cooling.js";
import {
  analyzeCoolantMargin,
} from "./coolant-margin.js";
import {
  analyzeDescentEgtDropout,
  analyzeTransientDivergence,
} from "./divergence.js";
import {
  analyzeDensityAltitudeContext,
} from "./density-altitude-context.js";
import {
  analyzeEgtBalance,
} from "./egt.js";
import {
  analyzeFlightCompleteness,
} from "./flight-completeness-confidence.js";
import { analyzePropellerRpm } from "./propeller-rpm.js";
import {
  analyzeFuelFlowResponse,
} from "./fuel-flow-response.js";
import { analyzeFuelPressure } from "./fuel-pressure.js";
import {
  analyzeFuelFlowWorkload,
} from "./fuel-flow-workload.js";
import {
  analyzeInductionLeak,
} from "./induction-leak.js";
import {
  analyzeInstrumentationSuspicion,
} from "./instrumentation.js";
import {
  analyzeElectrical,
  analyzeStartChargeMetric,
} from "./electrical.js";
import {
  analyzeMagCheck,
  candidateMagPhase,
} from "./mag.js";
import {
  analyzeOilSystem,
} from "./oil-system.js";
import {
  analyzeTakeoffEnrichment,
} from "./takeoff-enrichment.js";
import {
  analyzeOperationalNormalcy,
} from "./operational-normalcy.js";
import {
  analyzePhaseEvents,
} from "./phase-event.js";
import {
  analyzePatternWorkContext,
} from "./pattern-work-context.js";
import {
  analyzePowerChangeContext,
} from "./power-change-context.js";
import {
  analyzeRopLeaningContext,
} from "./rop-leaning-context.js";
import { analyzeLopLeaningContext } from "./lop-leaning-context.js";
import {
  analyzeSensorCoherence,
} from "./sensor-coherence.js";
import {
  analyzeStartupBehavior,
} from "./startup-behavior.js";
import {
  analyzeStablePowerQuality,
} from "./stable-power-quality.js";
import {
  augmentSensorValidityWithCoolingRate,
  analyzeSensorValidity,
  summarizeSensorChannel,
} from "./sensors.js";
import {
  analyzeTurboHealth,
} from "./turbo.js";
import {
  analyzeThermalContext,
} from "./thermal-context.js";
import {
  analyzeExhaustValvePattern,
} from "./valve.js";
import { metricSeverityClass } from "./metric-severity.js";

export const healthMetricLabels = Object.freeze({
  sensors: "Sensors",
  turbo: "Turbo",
  oilSystem: "Oil system",
  coolantMargin: "Coolant margin",
  takeoffEnrichment: "Takeoff enrichment",
  cht: "CHT heat",
  chtRhythm: "CHT rhythm",
  egt: "EGT balance",
  valve: "EGT rhythm",
  combustion: "CHT/EGT pairing",
  abnormalCombustion: "Abnormal combustion",
  divergence: "transient divergence",
  descentEgtDropout: "Descent EGT dropout",
  inductionLeak: "Induction leak",
  mag: "Mag check",
  carbIce: "Carb ice",
  cooling: "Cooling",
  coolingRate: "Cooling rate",
  thermalContext: "Thermal context",
  densityAltitudeContext: "Density Altitude Context",
  cylinderHarmony: "Cylinder harmony",
  cylinderStartupSymmetry: "Cylinder startup symmetry",
  cylinderPersonality: "Cylinder Personality",
  powerChangeContext: "Power change context",
  ropLeaningContext: "ROP leaning context",
  lopLeaningContext: "LOP leaning context",
  fuelFlowResponse: "Fuel flow response",
  fuelPressure: "Fuel pressure",
  propellerRpm: "Propeller RPM",
  fuelFlowWorkload: "Fuel-flow workload",
  stablePowerQuality: "Stable Power Quality",
  flightCompleteness: "Flight Completeness",
  sensorCoherence: "Sensor coherence",
  phaseEvents: "Phase events",
  patternWorkContext: "Pattern work context",
  startupBehavior: "Startup behavior",
  operationalNormalcy: "Operational Normalcy",
  charging: "Start charge",
  electrical: "Electrical",
  engineDelta: "Engine delta",
  instrumentationSuspicion: "Probe Reference",
});

export const healthMetricWeights = Object.freeze({
  sensors: 0.22,
  turbo: 0.2,
  oilSystem: 0.06,
  coolantMargin: 0.16,
  takeoffEnrichment: 0.16,
  cht: 0.28,
  egt: 0.18,
  valve: 0.23,
  abnormalCombustion: 0.2,
  inductionLeak: 0.14,
  mag: 0.14,
  carbIce: 0.14,
  cooling: 0.22,
  coolingRate: 0.18,
  charging: 0.14,
  electrical: 0.16,
  engineDelta: 0.16,
  instrumentationSuspicion: 0.10,
});

export const rotaxNotApplicableMetricKeys = Object.freeze([
  "cht",
  "chtRhythm",
  "combustion",
  "cylinderHarmony",
  "abnormalCombustion",
  "inductionLeak",
  "mag",
  "carbIce",
  "cooling",
  "coolingRate",
  "ropLeaningContext",
  "lopLeaningContext",
]);

function notApplicableMetric(key, applicability) {
  const familyLabel = applicability.engineFamily === "rotax" ? "Rotax" : "engine";
  return {
    key,
    score: 100,
    label: "Not applicable",
    note: "Not applicable",
    details: {
      status: "not-applicable",
      reason: `${familyLabel} engine context uses a different detector set.`,
      engineFamily: applicability.engineFamily,
      engineCooling: applicability.engineCooling,
      engineControl: applicability.engineControl,
    },
  };
}

export function analysisApplicabilityForOptions(options = {}) {
  const context = {
    aircraftProfile: options.aircraftProfile ?? null,
    parserMetadata: options.parserMetadata ?? null,
    engineFamily: options.engineFamily,
    engineCooling: options.engineCooling,
    engineControl: options.engineControl,
  };
  const engineFamily = inferEngineFamilyFromContext(context);
  const engineCooling = inferEngineCoolingTypeFromContext(context);
  const engineControl = inferEngineControlTypeFromContext(context);
  const disabledMetricKeys = isRotaxEngineContext(context) ? [...rotaxNotApplicableMetricKeys] : [];
  return {
    engineFamily,
    engineCooling,
    engineControl,
    disabledMetricKeys,
  };
}

function clampScore(score) {
  return Math.max(0, Math.min(100, Math.round(score)));
}

export function weightedMetricRows(metrics, scoringScope = "aircraft", disabledKeys = []) {
  const disabledMetricKeys = new Set(Array.isArray(disabledKeys) ? disabledKeys : []);
  return Object.entries(healthMetricWeights)
    .map(([key, weight]) => [key, metrics[key], weight])
    .filter(([key, metric]) => !disabledMetricKeys.has(key) && metricParticipatesInScopedScore(key, metric, scoringScope));
}

export function scoreFromWeightedMetricRows(rows) {
  return overallScoreDetailsFromWeightedMetricRows(rows).score;
}

export function overallScoreDetailsFromWeightedMetricRows(rows) {
  if (!rows.length) return { score: null, weightedScore: null, severityCeiling: null,
    note: "No enabled checks have enough data to score." };
  const totalWeight = rows.reduce((total, [, , weight]) => total + weight, 0) || 1;
  const weightedScore = Math.round(rows.reduce((total, [, metric, weight]) => total + metric.score * weight, 0) / totalWeight);
  const severities = rows.map(([key, metric]) => ({ key, className: metricSeverityClass(key, metric) }));
  const strongestClass = severities.some(entry => entry.className === "red") ? "red"
    : severities.some(entry => entry.className === "amber") ? "amber" : null;
  const severityCeiling = strongestClass ? {
    maxScore: strongestClass === "red" ? 71 : 87,
    className: strongestClass,
    metricKeys: severities.filter(entry => entry.className === strongestClass).map(entry => entry.key),
  } : null;
  const score = Math.min(weightedScore, severityCeiling?.maxScore ?? 100);
  const note = score < weightedScore
    ? `Overall reflects the ${severityCeiling.metricKeys.map(key => healthMetricLabels[key] ?? key).join(", ")} ${scoreLabel(score)} cue. The weighted average is ${weightedScore}.`
    : `Weighted average of ${rows.length} evaluated check${rows.length === 1 ? "" : "s"}.`;
  return { score, weightedScore, severityCeiling, note };
}

export function sensitivityAdjustedScore(score, sensitivity = "balanced") {
  if (!Number.isFinite(score)) return score;
  const penalty = 100 - score;
  const scale = sensitivityModes[normalizeSensitivity(sensitivity)].penaltyScale;
  const adjusted = clampScore(100 - penalty * scale);
  if (score <= 8 && sensitivity !== "sensitive") return Math.min(adjusted, 68);
  return adjusted;
}

function sensitivityAdjustedNote(metric, adjustedScore, sensitivity = "balanced") {
  if (!Number.isFinite(metric?.score) || metric.score === adjustedScore || sensitivity === "sensitive") return metric?.note;
  const mode = sensitivityModes[normalizeSensitivity(sensitivity)].label.toLowerCase();
  const label = healthMetricLabels[metric.key] ?? "This check";
  if (metric.key === "divergence" && adjustedScore < 88) return metric.note;
  if (metric.key === "charging" && metric.details?.status) return metric.note;
  if (metric.key === "cooling" && adjustedScore >= 88 && metric.score < 88) return "Cooling limited";
  if (metric.key === "valve" && adjustedScore >= 88 && metric.score < 88) return "EGT rhythm";
  if (metric.key === "chtRhythm" && adjustedScore >= 88 && metric.score < 88) return "CHT rhythm";
  if (adjustedScore >= 88 && metric.score < 88) return `Holds at ${mode}`;
  if (adjustedScore >= 72 && metric.score < 72) return `Watch cue at ${mode}`;
  return metric.note;
}

export function sensitivityAdjustedMetric(metric, sensitivity = "balanced") {
  if (!metric || !Number.isFinite(metric.score)) return metric;
  if (metric.key === "egt" && metric.details?.engines?.length > 1) {
    const engines = metric.details.engines.map(engine => ({ ...engine,
      ...sensitivityAdjustedMetric({ ...engine, key: "egt" }, sensitivity) }));
    const worst = engines.filter(engine => Number.isFinite(engine.score))
      .sort((a, b) => a.score - b.score || (b.details?.p90 ?? 0) - (a.details?.p90 ?? 0))[0];
    if (worst) return { ...metric, rawScore: metric.rawScore ?? metric.score, score: worst.score,
      details: { ...worst.details, engines, worstEngineKey: worst.engineKey } };
  }
  if (metric.key === "egt" && (metric.details?.hot?.isReview || metric.details?.sustainedEgtSeparation || metric.details?.sustainedLowEgt)) return metric;
  if (metric.key === "charging"
    || metric.key === "combustion"
    || metric.key === "chtRhythm"
    || metric.key === "coolingRate"
    || metric.key === "turbo"
    || metric.key === "oilSystem"
    || metric.key === "coolantMargin"
    || metric.key === "takeoffEnrichment"
    || metric.key === "stablePowerQuality"
    || metric.key === "operationalNormalcy") return metric;
  const eventCeiling = ["cht", "abnormalCombustion"].includes(metric.key) ? metric.details?.severityFloor?.maxScore : null;
  const score = Math.min(sensitivityAdjustedScore(metric.score, sensitivity), Number.isFinite(eventCeiling) ? eventCeiling : 100);
  const note = Number.isFinite(eventCeiling) ? metric.note : sensitivityAdjustedNote(metric, score, sensitivity);
  return {
    ...metric,
    rawScore: metric.rawScore ?? metric.score,
    score,
    note,
    // The sensitivity note explains why a SCORE moved ("Watch cue at
    // balanced") and is the right label for a score tile — but it overwrites
    // what the detector actually observed, and everything downstream (the
    // evidence packet, the AI summary, the report) then has a threshold
    // label where an observation should be. Keep the detector's own sentence
    // alongside it; consumers that want substance read this.
    detectorNote: metric.note && metric.note !== note ? metric.note : (metric.detectorNote ?? null),
  };
}

export function sensitivityAdjustedMetrics(metrics, sensitivity = "balanced") {
  return Object.fromEntries(Object.entries(metrics).map(([key, metric]) => [
    key,
    sensitivityAdjustedMetric(metric, sensitivity),
  ]));
}

function finiteCylinder(value) {
  return Number.isFinite(value) ? Math.round(value) : null;
}

function egtContextCylinderLabel(cylinder) {
  return Number.isFinite(cylinder) ? `EGT ${cylinder}` : "EGT";
}

function egtBalanceContextCue(metric) {
  const cue = metric?.details?.sustainedLowEgt;
  if (!cue) return null;
  const cylinder = finiteCylinder(cue.cylinder);
  return {
    source: "egtBalance",
    sourceLabel: "EGT Balance",
    cylinder,
    label: `${egtContextCylinderLabel(cylinder)} balance cue`,
    note: metric?.note ?? null,
    score: Number.isFinite(metric?.score) ? metric.score : null,
  };
}

function egtRhythmContextCue(metric) {
  if (!Number.isFinite(metric?.score) || metric.score >= 88) return null;
  const top = metric?.details?.top ?? {};
  const cylinder = finiteCylinder(top.cylinder);
  return {
    source: "egtRhythm",
    sourceLabel: "EGT Rhythm",
    cylinder,
    label: `${egtContextCylinderLabel(cylinder)} rhythm cue`,
    note: metric?.note ?? null,
    score: metric.score,
  };
}

function summarizeEgtContextCue(cues) {
  const balance = cues.find((cue) => cue.source === "egtBalance");
  const rhythm = cues.find((cue) => cue.source === "egtRhythm");
  if (balance && rhythm && Number.isFinite(balance.cylinder) && balance.cylinder === rhythm.cylinder) {
    return `${egtContextCylinderLabel(balance.cylinder)} balance/rhythm cue`;
  }
  return cues.map((cue) => cue.label).join(" · ");
}

function cylinderHarmonyEgtContextCue(rawMetrics) {
  const cues = [
    egtBalanceContextCue(rawMetrics?.egt),
    egtRhythmContextCue(rawMetrics?.valve),
  ].filter(Boolean);
  if (!cues.length) return null;
  return {
    limited: true,
    label: summarizeEgtContextCue(cues),
    cues,
  };
}

function annotateCylinderHarmonyEgtContext(rawMetrics) {
  const details = rawMetrics?.cylinderHarmony?.details;
  if (details?.engines?.length > 1) {
    details.engines = details.engines.map((entry) => {
      const engineMetric = (key) => {
        const match = rawMetrics[key]?.details?.engines?.find((candidate) => candidate.engineKey === entry.engineKey);
        return match?.result ?? match;
      };
      const cue = cylinderHarmonyEgtContextCue({ egt: engineMetric("egt"), valve: engineMetric("valve") });
      return { ...entry, details: { ...entry.details, egtContextCue: cue } };
    });
    details.egtContextCue = details.engines.find((entry) => entry.engineKey === details.worstEngineKey)?.details.egtContextCue ?? null;
    return rawMetrics;
  }
  const cue = cylinderHarmonyEgtContextCue(rawMetrics);
  if (!cue || !rawMetrics?.cylinderHarmony?.details) return rawMetrics;
  rawMetrics.cylinderHarmony = {
    ...rawMetrics.cylinderHarmony,
    details: {
      ...rawMetrics.cylinderHarmony.details,
      egtContextCue: cue,
    },
  };
  return rawMetrics;
}

// A CGR combo whose 30C clock could not be aligned with the 30P carries the 30C's
// channels for display only. Strip them before any detector runs so misaligned bus
// volts / amps / OAT samples can never feed scores, findings, trends, or AI summaries.
export function stripUnalignedAuxChannels(data = [], parserMetadata = null) {
  const twin = parserMetadata?.cgrTwinAlignment;
  if (twin && !twin.verified) {
    const keys = (twin.auxSeriesKeys ?? []).filter((key) => {
      const engine = key.endsWith("1") ? "left" : key.endsWith("2") ? "right" : null;
      return !engine || twin.sources.find((source) => source.role === engine)?.method !== "rpm-correlation";
    });
    return data.map((point) => {
      const next = { ...point };
      for (const key of keys) delete next[key];
      return next;
    });
  }
  if (parserMetadata?.comboAlignment !== "unaligned") return data;
  const auxKeys = Array.isArray(parserMetadata.auxSeriesKeys) ? parserMetadata.auxSeriesKeys : [];
  if (!auxKeys.length) return data;
  return data.map((point) => {
    const next = { ...point };
    for (const key of auxKeys) delete next[key];
    return next;
  });
}

export function computeRawAnalysisMetrics(rawData = [], options = {}) {
  const aircraftProfile = options.aircraftProfile ?? null;
  const parserMetadata = options.parserMetadata ?? null;
  const data = stripUnalignedAuxChannels(rawData, parserMetadata);
  const applicability = analysisApplicabilityForOptions({
    ...options,
    aircraftProfile,
    parserMetadata,
  });
  const notApplicableKeys = new Set(applicability.disabledMetricKeys);
  const perfMark = typeof options.perfMark === "function" ? options.perfMark : null;
  const now = () => (typeof performance !== "undefined" && performance?.now ? performance.now() : Date.now());
  const time = perfMark
    ? (label, fn) => {
        const t0 = now();
        try { return fn(); }
        finally { perfMark(label, now() - t0); }
      }
    : (_label, fn) => fn();
  const engineDescriptors = time("d:engineDescriptors", () => engineDescriptorsForData(data, aircraftProfile, {
    parserId: parserMetadata?.parserId ?? options.parserId ?? options.selectedEntry?.parser,
    parserMetadata,
    engineLayout: options.engineLayout ?? parserMetadata?.engineLayout,
  }));
  const analyzeMetric = (key, label, fn) => notApplicableKeys.has(key)
    ? notApplicableMetric(key, applicability)
    : time(label, fn);
  const labelForKey = options.labelForKey
    ?? ((key) => engineScopedProbeLabel(key, engineDescriptors) || rawMetricDescriptor(key)[0]);
  const oilLabelForKey = options.oilLabelForKey
    ?? ((key) => engineScopedChannelLabel(key, engineDescriptors) ?? rawMetricDescriptor(key)[0]);
  const summarizeSensorChannelForScope = (rows, key, peerKeys) => summarizeSensorChannel(rows, key, peerKeys, { labelForKey });
  const electrical = time("d:electrical", () => analyzeElectrical(data));
  const charging = time("d:charging", () => analyzeStartChargeMetric(electrical));
  const coolingRateThresholdFpm = Number.isFinite(Number(options.coolingRateThresholdFpm))
    ? Number(options.coolingRateThresholdFpm)
    : undefined;
  const coolingRate = analyzeMetric("coolingRate", "d:coolingRate", () => analyzeChtCoolingRate(data, { thresholdFpm: coolingRateThresholdFpm }));
  const sensors = time("d:sensors", () => {
    const metric = analyzePerEngineContext(data, engineDescriptors, (rows, engine) => {
      const scopedCoolingRate = engineDescriptors.length > 1 ? {
        ...coolingRate,
        details: { ...coolingRate.details, summaries: coolingRate.details?.summaries?.filter((summary) =>
          engine.cylinders.some((cylinder) => cylinder.chtKey === summary.key)) },
      } : coolingRate;
      return augmentSensorValidityWithCoolingRate(
        analyzeSensorValidity(rows, { ...options, aircraftProfile, parserMetadata, labelForKey, engineDescriptors: [engine] }),
        scopedCoolingRate,
      );
    });
    if (engineDescriptors.length > 1) {
      metric.details.summaries = metric.details.engines.flatMap((entry) => (entry.details.summaries ?? []).map((summary) => ({
        ...summary, engineKey: entry.engineKey, engineLabel: entry.label,
      }))).sort((a, b) => b.suspicion - a.suspicion);
      metric.details.top = metric.details.summaries[0] ?? null;
      metric.details.channelCount = metric.details.summaries.length;
    }
    return metric;
  });
  // Sensor augmentation is the last reader of coolingRate's raw per-window event
  // lists; collapse them to clusters now so a densely-sampled flight doesn't
  // persist a multi-hundred-MB analysis artifact (see cooling.ts). Scores are
  // unaffected — eventCount is a cluster count.
  compactCoolingRateSummaryEvents(coolingRate);
  const instrumentationSuspicion = time("d:instrumentation", () => analyzePerEngineContext(data, engineDescriptors, (rows, engine) => analyzeInstrumentationSuspicion(rows, {
    ...options, aircraftProfile, parserMetadata,
    sensorSummaries: engineDescriptors.length > 1
      ? sensors.details.engines.find((entry) => entry.engineKey === engine.engineKey)?.details.summaries
      : sensors?.details?.summaries,
  })));
  const includeContext = options.skipContextDetectors !== true;
  const cardsOnly = includeContext && options.contextCardsOnly === true;
  const egt = analyzeMetric("egt", "d:egt", () => analyzeEgtBalance(data, {
    summarizeSensorChannel: summarizeSensorChannelForScope,
    labelForKey,
    engineDescriptors,
  }));
  const rawMetrics = {
    sensors,
    turbo: analyzeMetric("turbo", "d:turbo", () => analyzeTurboHealth(data, {
      ...options,
      aircraftProfile,
      engineDescriptors,
    })),
    oilSystem: analyzeMetric("oilSystem", "d:oilSystem", () => analyzeOilSystem(data, { ...options, aircraftProfile, engineDescriptors, labelForKey: oilLabelForKey })),
    coolantMargin: analyzeMetric("coolantMargin", "d:coolantMargin", () => analyzeCoolantMargin(data, { ...options, aircraftProfile, parserMetadata })),
    takeoffEnrichment: analyzeMetric("takeoffEnrichment", "d:takeoffEnrichment", () => analyzeTakeoffEnrichment(data, { ...options, aircraftProfile, engineDescriptors })),
    cht: analyzeMetric("cht", "d:cht", () => analyzeChtHealth(data, { engineDescriptors })),
    egt,
    valve: analyzeMetric("valve", "d:valve", () => analyzeExhaustValvePattern(data, {
      ...options,
      aircraftProfile,
      staticValveRowsForAircraft: options.staticValveRowsForAircraft ?? (() => []),
      trendDateValue: options.trendDateValue,
      summarizeSensorChannel: summarizeSensorChannelForScope,
      labelForKey,
      formatPointTime: options.formatPointTime,
      phaseForWindow: options.phaseForWindow ?? candidateMagPhase,
      engineDescriptors,
    })),
    abnormalCombustion: analyzeMetric("abnormalCombustion", "d:abnormalCombustion", () => analyzeAbnormalCombustion(data, { engineDescriptors, sensorSummaries: sensors?.details?.summaries, egtBalance: egt })),
    inductionLeak: analyzeMetric("inductionLeak", "d:inductionLeak", () => analyzeInductionLeak(data, { sensorSummaries: sensors?.details?.summaries, engineDescriptors })),
    mag: analyzeMetric("mag", "d:mag", () => analyzeMagCheck(data, { engineDescriptors })),
    carbIce: analyzeMetric("carbIce", "d:carbIce", () => analyzeCarbIce(data)),
    cooling: analyzeMetric("cooling", "d:cooling", () => analyzeCoolingEfficiency(data, { engineDescriptors })),
    coolingRate,
    ...(includeContext ? (() => {
      // Card-tier: the only context detectors the dashboard overview renders
      // (operating-context cards). Cheap, and computed in both cards-only and full modes.
      const cardContext = {
        divergence: time("d:divergence", () => analyzeTransientDivergence(data, { engineDescriptors })),
        descentEgtDropout: time("d:descentEgtDropout", () => analyzeDescentEgtDropout(data, { engineDescriptors })),
        combustion: analyzeMetric("combustion", "d:combustion", () => analyzeCombustionSignatures(data, { engineDescriptors })),
        chtRhythm: analyzeMetric("chtRhythm", "d:chtRhythm", () => analyzeChtRhythm(data, {
          ...options,
          engineDescriptors,
          labelForKey,
          formatPointTime: options.formatPointTime,
          phaseForWindow: options.phaseForWindow ?? candidateMagPhase,
        })),
        cylinderHarmony: analyzeMetric("cylinderHarmony", "d:cylinderHarmony", () => analyzeCylinderHarmony(data, { engineDescriptors })),
        ropLeaningContext: analyzeMetric("ropLeaningContext", "d:ropLeaningContext", () => analyzePerEngineContext(data, engineDescriptors, analyzeRopLeaningContext)),
        lopLeaningContext: analyzeMetric("lopLeaningContext", "d:lopLeaningContext", () => analyzePerEngineContext(data, engineDescriptors, (rows, engine) => analyzeLopLeaningContext(rows, {
          ...aircraftProfile,
          engineLayout: aircraftProfile?.engineLayout ?? options.engineLayout ?? parserMetadata?.engineLayout,
        }, engine))),
        propellerRpm: time("d:propellerRpm", () => analyzePropellerRpm(data, { aircraftProfile, parserMetadata, engineDescriptors })),
        fuelFlowResponse: time("d:fuelFlowResponse", () => analyzeFuelFlowResponse(data, { aircraftProfile, parserMetadata, engineDescriptors })),
        fuelPressure: time("d:fuelPressure", () => analyzeFuelPressure(data, { aircraftProfile, parserMetadata, engineDescriptors })),
      };
      if (cardsOnly) return cardContext;
      // AI-tier: consumed only when the full analysis run is built (AI summary / persistence).
      const phaseEventsMetric = time("d:phaseEvents", () => analyzePhaseEvents(data, { engineDescriptors }));
      const startupBehaviorMetric = time("d:startupBehavior", () => analyzePerEngineContext(data, engineDescriptors, (rows) => analyzeStartupBehavior(rows, { electrical: engineDescriptors.length > 1 ? analyzeElectrical(rows) : electrical })));
      const patternWorkContextMetric = time("d:patternWorkContext", () => analyzePatternWorkContext(data));
      const powerChangeContextMetric = time("d:powerChangeContext", () => analyzePerEngineContext(data, engineDescriptors, analyzePowerChangeContext));
      const sensorCoherenceMetric = time("d:sensorCoherence", () => analyzeSensorCoherence(data, {
        sensors,
        phaseEvents: phaseEventsMetric,
        powerChangeContext: powerChangeContextMetric,
        ropLeaningContext: cardContext.ropLeaningContext,
      }));
      const stablePowerQualityMetric = time("d:stablePowerQuality", () => analyzeStablePowerQuality(data, {
        ...options,
        aircraftProfile,
        sensors,
        sensorCoherence: sensorCoherenceMetric,
        instrumentationSuspicion,
        phaseEvents: phaseEventsMetric,
      }));
      return {
        ...cardContext,
        thermalContext: time("d:thermalContext", () => analyzePerEngineContext(data, engineDescriptors, analyzeThermalContext)),
        densityAltitudeContext: time("d:densityAltitudeContext", () => analyzeDensityAltitudeContext(data)),
        cylinderStartupSymmetry: time("d:cylinderStartupSymmetry", () => analyzePerEngineContext(data, engineDescriptors, (rows, engine) => analyzeCylinderStartupSymmetry(rows, {
          startupBehavior: engineDescriptors.length > 1 ? startupBehaviorMetric.details.engines.find((entry) => entry.engineKey === engine.engineKey) : startupBehaviorMetric,
          phaseEvents: engineDescriptors.length > 1 ? analyzePhaseEvents(rows) : phaseEventsMetric,
          startCharge: engineDescriptors.length > 1 ? analyzeStartChargeMetric(analyzeElectrical(rows)) : charging,
        }))),
        powerChangeContext: powerChangeContextMetric,
        fuelFlowWorkload: time("d:fuelFlowWorkload", () => analyzeFuelFlowWorkload(data, {
          patternWorkContext: patternWorkContextMetric,
          phaseEvents: phaseEventsMetric,
        })),
        flightCompleteness: time("d:flightCompleteness", () => analyzeFlightCompleteness(data, {
          aircraftProfile,
          parserMetadata,
          sensors,
          stablePowerQuality: stablePowerQualityMetric,
          phaseEvents: phaseEventsMetric,
          engineDescriptors,
        })),
        stablePowerQuality: stablePowerQualityMetric,
        sensorCoherence: sensorCoherenceMetric,
        phaseEvents: phaseEventsMetric,
        patternWorkContext: patternWorkContextMetric,
        startupBehavior: startupBehaviorMetric,
      };
    })() : {}),
    charging,
    electrical,
    engineDelta: time("d:engineDelta", () => {
      // Separate-monitor twins: recorded-clock timing gates the comparison,
      // owner-set (manual) timing runs it and is labeled as such.
      const timing = cgrTwinTimingProvenance(parserMetadata?.cgrTwinAlignment);
      return analyzeCrossEngineComparison(data, engineDescriptors, timing
        ? { timingVerified: timing !== "unverified", timingProvenance: timing }
        : {});
    }),
    instrumentationSuspicion,
  };
  if (includeContext && !cardsOnly) {
    rawMetrics.cylinderPersonality = time("d:cylinderPersonality", () => analyzeCylinderPersonality(data, {
      sensors: rawMetrics.sensors,
      sensorCoherence: rawMetrics.sensorCoherence,
      instrumentationSuspicion: rawMetrics.instrumentationSuspicion,
      cht: rawMetrics.cht,
      egt: rawMetrics.egt,
      valve: rawMetrics.valve,
      combustion: rawMetrics.combustion,
      cooling: rawMetrics.cooling,
      coolingRate: rawMetrics.coolingRate,
      inductionLeak: rawMetrics.inductionLeak,
      engineDescriptors,
    }));
  }
  if (Number.isFinite(rawMetrics.instrumentationSuspicion?.score) && rawMetrics.instrumentationSuspicion.score < 88 && rawMetrics.sensors?.details) {
    const instrumentationDetails = rawMetrics.instrumentationSuspicion.details ?? {};
    rawMetrics.sensors.details.sharedReferenceCue = {
      engineKey: instrumentationDetails.worstEngineKey ?? null,
      engineLabel: instrumentationDetails.engineLabel ?? null,
      window: instrumentationDetails.window ?? null,
      powerChannels: instrumentationDetails.powerChannels ?? [],
      flaggedChannels: instrumentationDetails.flaggedChannels ?? [],
      flaggedEgtCount: instrumentationDetails.flaggedEgtCount ?? 0,
      flaggedChtCount: instrumentationDetails.flaggedChtCount ?? 0,
      flaggedTitCount: instrumentationDetails.flaggedTitCount ?? 0,
      synchronyRatio: instrumentationDetails.synchronyRatio ?? null,
      longestOverlapSeconds: instrumentationDetails.longestOverlapSeconds ?? null,
      score: rawMetrics.instrumentationSuspicion.score,
    };
  }
  annotateCylinderHarmonyEgtContext(rawMetrics);
  if (includeContext && !cardsOnly) {
    rawMetrics.operationalNormalcy = time("d:operationalNormalcy", () => analyzeOperationalNormalcy(data, {
      ...options,
      aircraftProfile,
      parserMetadata,
      engineDescriptors,
      metrics: rawMetrics,
    }));
  }
  return { rawMetrics, engineDescriptors, applicability };
}

export function finalizeAnalysisSummary(rawBundle, options = {}) {
  const { rawMetrics, engineDescriptors } = rawBundle;
  const applicability = rawBundle.applicability ?? analysisApplicabilityForOptions(options);
  const sensitivity = normalizeSensitivity(options.sensitivity);
  const metrics = sensitivityAdjustedMetrics(rawMetrics, sensitivity);
  const scoringScope = normalizeHealthMetricScope(options.metricScoreScope === "engine" ? "engine" : "aircraft");
  const disabledMetricKeys = [...new Set([
    ...applicability.disabledMetricKeys,
    ...(Array.isArray(options.disabledMetricKeys) ? options.disabledMetricKeys : []),
  ]
    .map((key) => String(key ?? "").trim())
    .filter(Boolean))];
  const scored = weightedMetricRows(metrics, scoringScope, disabledMetricKeys);
  const scoreDetails = overallScoreDetailsFromWeightedMetricRows(scored);
  const overall = {
    ...scoreDetails,
    label: scoreLabel(scoreDetails.score),
    className: scoreClass(scoreDetails.score),
    scoringScope,
    contributingMetrics: scored.map(([key]) => key),
    disabledMetricKeys,
  };
  return {
    overall,
    ...metrics,
    sensitivity,
    engineDescriptors,
    applicability,
    metricScopes: Object.fromEntries(Object.keys(healthMetricLabels).map((key) => [key, healthMetricScopeForMetric(key, metrics[key])])),
    finding: typeof options.buildFinding === "function" ? options.buildFinding(metrics, { sensitivity, disabledMetricKeys }) : null,
  };
}

export function calculateAnalysisSummary(data = [], options = {}) {
  return finalizeAnalysisSummary(computeRawAnalysisMetrics(data, options), options);
}
